import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Grid, Container, Paper, Theme, styled } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import withQuillLayout from 'src/_common/hoc/QuillLayout';
import TopControlBar from 'src/word-counter/component/TopControlBar';
import { RootState } from 'src/_common/models/redux/store';
import { useSelector } from 'react-redux';
import Head from 'next/head';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { TinyMCELogo } from '@Common/quillElements/icon';
import { handleHotKeys } from 'src/word-counter/handlers/hotkeys';
import WordCounterExplanation from 'src/word-counter/component/WordCounterExplanation';

const EditorBox = dynamic(() => import('src/word-counter/component/EditorBox'), { ssr: false });
const BottomControlBar = dynamic(() => import('src/word-counter/component/BottomControlBar'), {
  ssr: false,
});

const useStyles = makeStyles<Theme, { isDrawerOpen: boolean }>((theme) => ({
  bgColorbody: {
    backgroundColor: theme.palette.background.default,
    paddingBottom: '20px !important',
    paddingLeft: (props) => (props.isDrawerOpen ? '24px' : '42px'),
    paddingRight: (props) => (props.isDrawerOpen ? '24px' : '42px'),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '24px',
      paddingRight: '24px',
    },
  },
  wordCounterHeader: {
    position: 'sticky',
    top: 0,
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.divider,
    zIndex: 1,
    backgroundColor: theme.palette.grey[50],
  },

  wordCounterFooter: {
    backgroundColor: theme.palette.grey[50],
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
  },
}));

const StyledRootContainer = styled(Container)`
  && {
    padding-top: 25px;
    padding-bottom: 30px;
    flex-direction: column;
    flex-wrap: unset !important;
    display: flex;
  }
`;

function Editor() {
  const { isMobile, native } = useSelector((state: RootState) => state.common.platform);
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension) || false;
  const extensionName = useSelector(
    (state: RootState) => state.common.platform.extensionMetaData?.type
  );
  const quillDrawer = useSelector((state: RootState) => state.common.quillDrawer);
  const [inputText, setInputText] = useState('');

  // used this for extension & mobile only
  let editorBoxContainerHeight = 'calc(100vh - 88px)';
  if (isExtension) {
    if (extensionName === 'chrome') editorBoxContainerHeight = 'calc(100vh - 93px)';
    else if (extensionName === 'google-docs') editorBoxContainerHeight = 'calc(100vh - 93px)';
    else if (extensionName === 'ms-word') editorBoxContainerHeight = 'calc(100vh - 93px)';
  }

  useEffect(() => {
    document.addEventListener('keydown', handleHotKeys);
    return () => {
      document.removeEventListener('keydown', handleHotKeys);
    };
  }, []);

  const classes = useStyles({ isDrawerOpen: !!quillDrawer });
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          key="company-schema"
          // dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </Head>
      {!isMobile && !isExtension && (
        <StyledRootContainer
          maxWidth={false}
          className={`root-container home-container-gray ${classes.bgColorbody}`}
        >
          <Grid container alignItems="center" justifyContent="center">
            <Grid item xs={12} className="mid-container">
              <Paper
                elevation={3}
                style={{
                  borderRadius: '0px',
                  margin: '12px auto',
                }}
              >
                <Grid container>
                  <Grid container className={classes.wordCounterHeader} justifyContent="center">
                    <TopControlBar inputText={inputText} />
                  </Grid>
                  <Grid container style={{ minHeight: '50vh' }}>
                    <EditorBox
                      inputText={inputText}
                      setInputText={setInputText}
                      isExtension={isExtension}
                    />
                  </Grid>
                  <Grid container className={classes.wordCounterFooter} justifyContent="center">
                    <BottomControlBar inputText={inputText} />
                  </Grid>
                </Grid>
              </Paper>
              <Grid container justifyContent="flex-end">
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://www.tiny.cloud/"
                  style={{ maxHeight: '20px' }}
                >
                  <TinyMCELogo style={{ width: '2em' }} />
                </a>
              </Grid>
            </Grid>
          </Grid>
        </StyledRootContainer>
      )}

      {(isMobile || isExtension) && (
        <Grid container className="nd-mobile-container" alignItems="center">
          <Grid container direction="column">
            <Grid
              item
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 9,
              }}
            >
              <Grid container direction="column">
                <Grid item>
                  <TopControlBar inputText={inputText} />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              style={{
                minHeight: editorBoxContainerHeight,
              }}
            >
              <EditorBox
                setInputText={setInputText}
                isExtension={isExtension}
                inputText={inputText}
              />
            </Grid>
            <Grid item style={{ position: 'sticky', bottom: 0, width: '100%' }}>
              <BottomControlBar inputText={inputText} />
            </Grid>
            <Grid
              container
              justifyContent="flex-end"
              style={{ paddingTop: '10px', paddingRight: '10px' }}
            >
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://www.tiny.cloud/"
                style={{ maxHeight: '20px' }}
              >
                <TinyMCELogo style={{ width: '2.25em' }} />
              </a>
            </Grid>
          </Grid>
        </Grid>
      )}
      <WordCounterExplanation />
    </>
  );
}

export default withQuillLayout({
  children: Editor,
});
